import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Eye,
  Loader2,
  Search,
  FileText,
  TrendingUp,
  TrendingDown,
  Activity,
  Filter,
  X,
  Trash2,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const statColor = (score) => {
  if (score >= 81) return "#22B673";
  if (score >= 61) return "#6B8DB3";
  if (score >= 41) return "#F5A623";
  return "#B9262B";
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    consultant: "",
    min_score: "",
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/diagnostics/${toDelete.id}`);
      toast.success(`Diagnóstico de "${toDelete.company}" excluído.`);
      setItems((prev) => prev.filter((x) => x.id !== toDelete.id));
      setToDelete(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  const buildQuery = () => {
    const params = new URLSearchParams();
    if (filters.start_date) params.set("start_date", filters.start_date);
    if (filters.end_date) params.set("end_date", filters.end_date);
    if (filters.consultant) params.set("consultant", filters.consultant);
    if (filters.min_score !== "" && filters.min_score !== null)
      params.set("min_score", filters.min_score);
    const s = params.toString();
    return s ? `?${s}` : "";
  };

  const load = async () => {
    setLoading(true);
    try {
      const [list, st, opts] = await Promise.all([
        api.get(`/diagnostics${buildQuery()}`),
        api.get("/dashboard/stats"),
        consultants.length === 0
          ? api.get("/options?type=consultant")
          : Promise.resolve({ data: null }),
      ]);
      setItems(list.data || []);
      setStats(st.data);
      if (opts.data) setConsultants(opts.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
  }, []);

  const applyFilters = () => {
    load();
  };

  const clearFilters = () => {
    setFilters({ start_date: "", end_date: "", consultant: "", min_score: "" });
    setTimeout(load, 0);
  };

  const activeFilterCount =
    (filters.start_date ? 1 : 0) +
    (filters.end_date ? 1 : 0) +
    (filters.consultant ? 1 : 0) +
    (filters.min_score !== "" ? 1 : 0);

  const filtered = useMemo(() => {
    if (!q) return items;
    const s = q.toLowerCase();
    return items.filter(
      (it) =>
        (it.company || "").toLowerCase().includes(s) ||
        (it.cnpj || "").toLowerCase().includes(s) ||
        (it.consultant || "").toLowerCase().includes(s)
    );
  }, [items, q]);

  const sectionsWithData = useMemo(
    () => (stats?.by_section || []).filter((s) => s.samples > 0),
    [stats]
  );

  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.32em] text-[#6B8DB3]">
        Painel administrativo
      </div>
      <h1 className="text-3xl font-bold tracking-tight">Visão Geral</h1>
      <p className="text-[#9aa3ad] mt-1">
        Indicadores agregados e diagnósticos respondidos.
      </p>

      {/* Top stats */}
      <div className="grid sm:grid-cols-3 gap-4 mt-8">
        <StatCard
          label="Diagnósticos respondidos"
          value={stats?.total_diagnostics ?? 0}
          color="#6B8DB3"
          icon={Activity}
        />
        <StatCard
          label="Score médio geral"
          value={`${Math.round(stats?.avg_total_score ?? 0)}%`}
          color={statColor(stats?.avg_total_score ?? 0)}
          icon={TrendingUp}
        />
        <StatCard
          label="Sessão com menor score"
          value={stats?.worst_sections?.[0]?.name || "—"}
          color="#B9262B"
          icon={TrendingDown}
          small
        />
      </div>

      {/* Score por sessão */}
      <div className="mt-8 p-6 rounded-2xl border border-[#3A3F47] bg-[#1C1F26]">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#6B8DB3] mb-1">
              Indicadores agregados
            </div>
            <h2 className="text-xl font-semibold">Score médio por Sessão</h2>
          </div>
          <div className="text-xs text-[#6B8DB3]">
            Média de todos os {stats?.total_diagnostics ?? 0} diagnósticos
          </div>
        </div>

        {sectionsWithData.length === 0 ? (
          <div className="py-8 text-center text-[#6B8DB3] text-sm">
            Nenhum diagnóstico respondido ainda.
          </div>
        ) : (
          <div className="space-y-4">
            {sectionsWithData.map((s) => (
              <div key={s.section_id}>
                <div className="flex justify-between items-baseline text-sm mb-1.5">
                  <span className="text-white">{s.name}</span>
                  <span className="font-mono text-xs">
                    <span style={{ color: statColor(s.avg_score) }}>
                      {Math.round(s.avg_score)}%
                    </span>
                    <span className="text-[#6B8DB3]"> · n={s.samples}</span>
                  </span>
                </div>
                <div className="h-2.5 bg-[#0e1015] rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{
                      width: `${s.avg_score}%`,
                      background: statColor(s.avg_score),
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Diagnostics list */}
      <h2 className="mt-12 text-xl font-semibold">Diagnósticos respondidos</h2>
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B8DB3]"
          />
          <Input
            data-testid="diagnostics-search"
            placeholder="Buscar por empresa, CNPJ ou consultor..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="bg-[#1C1F26] border-[#3A3F47] text-white h-11 pl-9"
          />
        </div>
        <Button
          onClick={() => setFiltersOpen((v) => !v)}
          variant="outline"
          data-testid="toggle-filters-btn"
          className="border-[#3A3F47] text-white hover:bg-[#1C1F26] h-11"
        >
          <Filter size={14} className="mr-2" />
          Filtros
          {activeFilterCount > 0 && (
            <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#B9262B] text-white text-[10px] font-bold">
              {activeFilterCount}
            </span>
          )}
        </Button>
        {activeFilterCount > 0 && (
          <Button
            variant="ghost"
            onClick={clearFilters}
            className="text-[#6B8DB3] hover:text-white hover:bg-[#1C1F26] h-11"
          >
            <X size={14} className="mr-1" /> Limpar
          </Button>
        )}
      </div>

      {filtersOpen && (
        <div className="mt-4 p-5 rounded-xl border border-[#3A3F47] bg-[#1C1F26] fade-slide">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#6B8DB3] mb-1.5 block">
                Data inicial
              </label>
              <Input
                type="date"
                data-testid="filter-start-date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                className="bg-[#0e1015] border-[#3A3F47] text-white h-10"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#6B8DB3] mb-1.5 block">
                Data final
              </label>
              <Input
                type="date"
                data-testid="filter-end-date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                className="bg-[#0e1015] border-[#3A3F47] text-white h-10"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#6B8DB3] mb-1.5 block">
                Consultor
              </label>
              <Select
                value={filters.consultant || "all"}
                onValueChange={(v) =>
                  setFilters({ ...filters, consultant: v === "all" ? "" : v })
                }
              >
                <SelectTrigger
                  data-testid="filter-consultant"
                  className="bg-[#0e1015] border-[#3A3F47] text-white h-10"
                >
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent className="bg-[#1C1F26] border-[#3A3F47] text-white">
                  <SelectItem value="all">Todos</SelectItem>
                  {consultants.map((c) => (
                    <SelectItem key={c.id} value={c.value}>
                      {c.value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#6B8DB3] mb-1.5 block">
                Score mínimo (%)
              </label>
              <Input
                type="number"
                min={0}
                max={100}
                data-testid="filter-min-score"
                value={filters.min_score}
                onChange={(e) => setFilters({ ...filters, min_score: e.target.value })}
                placeholder="0"
                className="bg-[#0e1015] border-[#3A3F47] text-white h-10"
              />
            </div>
          </div>
          <div className="flex justify-end mt-4 gap-2">
            <Button
              variant="ghost"
              onClick={clearFilters}
              className="text-[#9aa3ad] hover:text-white"
            >
              Limpar
            </Button>
            <Button
              onClick={applyFilters}
              data-testid="apply-filters-btn"
              className="bg-[#B9262B] hover:bg-[#9a1e22] text-white"
            >
              Aplicar filtros
            </Button>
          </div>
        </div>
      )}

      <div className="mt-6 border border-[#3A3F47] rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#6B8DB3]">
            <Loader2 className="animate-spin inline mr-2" /> Carregando...
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-[#6B8DB3]">
            <FileText className="inline mr-2" />
            Nenhum diagnóstico encontrado.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#0e1015] text-[#6B8DB3] text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-4">Empresa</th>
                <th className="text-left p-4 hidden md:table-cell">CNPJ</th>
                <th className="text-left p-4 hidden md:table-cell">Consultor</th>
                <th className="text-left p-4">Score</th>
                <th className="text-left p-4 hidden lg:table-cell">Nível</th>
                <th className="text-left p-4 hidden lg:table-cell">Data</th>
                <th className="text-right p-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => {
                const score = Math.round(d.scoring?.total || 0);
                return (
                  <tr
                    key={d.id}
                    className="border-t border-[#1C1F26] hover:bg-[#1C1F26]/50 transition-colors"
                    data-testid={`diagnostic-row-${d.id}`}
                  >
                    <td className="p-4 font-medium text-white">
                      {d.company}
                      {d.status === "parcial_pre_diagnostico" && (
                        <span
                          className="ml-2 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-[#F5A623]/15 text-[#F5A623] border border-[#F5A623]/40 align-middle"
                          title="O usuário encerrou a jornada após o pré-diagnóstico"
                        >
                          Pré-diagnóstico apenas
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-[#9aa3ad] hidden md:table-cell">
                      {d.cnpj || "—"}
                    </td>
                    <td className="p-4 text-[#9aa3ad] hidden md:table-cell">
                      {d.consultant || "—"}
                    </td>
                    <td className="p-4">
                      <span
                        className="font-mono font-semibold"
                        style={{ color: statColor(score) }}
                      >
                        {score}%
                      </span>
                    </td>
                    <td className="p-4 text-[#E6E6E8] hidden lg:table-cell">
                      {d.scoring?.level || "—"}
                    </td>
                    <td className="p-4 text-[#6B8DB3] text-xs font-mono hidden lg:table-cell">
                      {d.created_at
                        ? new Date(d.created_at).toLocaleDateString("pt-BR")
                        : "—"}
                    </td>
                    <td className="p-4 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          onClick={() => navigate(`/admin/diagnosticos/${d.id}`)}
                          data-testid={`view-diagnostic-${d.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#1C1F26] hover:bg-[#252932] border border-[#3A3F47]"
                        >
                          <Eye size={14} /> Ver
                        </button>
                        <button
                          onClick={() => setToDelete({ id: d.id, company: d.company })}
                          data-testid={`delete-diagnostic-${d.id}`}
                          title="Excluir diagnóstico"
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-[#9aa3ad] hover:text-[#B9262B] hover:bg-[#B9262B]/10 border border-[#3A3F47]"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && !deleting && setToDelete(null)}>
        <AlertDialogContent
          data-testid="delete-diagnostic-dialog"
          className="bg-[#1C1F26] border-[#3A3F47] text-white"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Excluir diagnóstico?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#9aa3ad]">
              Esta ação é permanente. Todos os dados, respostas e propostas vinculadas a{" "}
              <strong className="text-white">{toDelete?.company}</strong> serão removidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={deleting}
              className="bg-[#0e1015] border-[#3A3F47] text-white hover:bg-[#252932] hover:text-white"
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              data-testid="confirm-delete-diagnostic-btn"
              className="bg-[#B9262B] hover:bg-[#9a1e22] text-white"
            >
              {deleting ? <Loader2 className="animate-spin" size={16} /> : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const StatCard = ({ label, value, color, icon: Icon, small }) => (
  <div className="p-5 rounded-xl border border-[#3A3F47] bg-[#1C1F26]">
    <div className="flex items-center justify-between mb-2">
      <div className="text-[10px] uppercase tracking-[0.32em] text-[#6B8DB3]">
        {label}
      </div>
      {Icon && <Icon size={16} style={{ color }} />}
    </div>
    <div
      className={small ? "text-lg font-semibold truncate" : "text-3xl font-bold"}
      style={{ color }}
      title={typeof value === "string" ? value : undefined}
    >
      {value}
    </div>
  </div>
);

export default AdminDashboard;
